import moment from 'moment';
import { useContext, useMemo } from 'react';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { FieldTypes } from '@ubeya/shared/hooks/account/useFields';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import useCRM from '@ubeya/shared/hooks/account/useCRM';
import useDepartments from '@ubeya/shared/hooks/account/useDepartments';
import useEmploymentTypes from '@ubeya/shared/hooks/generic/useEmploymentTypes';
import useLocations from '@ubeya/shared/hooks/account/useLocations';

const FIELD_SLUGS = {
  // Fields that are in GENERIC_MULTI_SELECT_FIELDS
  POSITIONS: 'positions',
  BRANCHES: 'branches',
  PREFERRED_CLIENTS: 'preferredClients',
  BLOCKED_CLIENTS: 'blockedClients',
  PREFERRED_AREA_IDS: 'preferredAreaIds',

  // Fields that are in GENERIC_SINGLE_SELECT_FIELDS
  EMPLOYMENT_TYPE_ID: 'employmentTypeId',
  DEPARTMENT_ID: 'departmentId'
};

// Generic fields that have a specific handling of the audit log old/new value
const GENERIC_MULTI_SELECT_FIELDS = [
  FIELD_SLUGS.BRANCHES,
  FIELD_SLUGS.POSITIONS,
  FIELD_SLUGS.BLOCKED_CLIENTS,
  FIELD_SLUGS.PREFERRED_CLIENTS,
  FIELD_SLUGS.PREFERRED_AREA_IDS
];

const GENERIC_SINGLE_SELECT_FIELDS = [FIELD_SLUGS.EMPLOYMENT_TYPE_ID, FIELD_SLUGS.DEPARTMENT_ID];

const getProfileFieldValueByMapping = ({
  t,
  profileFieldName,
  profileFieldType,
  profileFieldValue,
  mapping,
  dateFormat
}) => {
  if (profileFieldValue === undefined || profileFieldValue === null || profileFieldValue === '') {
    return t('empty');
  }

  switch (profileFieldType) {
    case FieldTypes.FIELD_TYPE_DATE:
      return moment(profileFieldValue).isValid() ? moment(profileFieldValue).format(dateFormat) : '';
    case FieldTypes.FIELD_TYPE_BOOLEAN:
      return profileFieldValue ? t('on') : t('off');
    case FieldTypes.FIELD_TYPE_OPTIONS:
      if (GENERIC_MULTI_SELECT_FIELDS.includes(profileFieldName)) {
        return profileFieldValue
          .split(',')
          .map((id) => mapping?.[id]?.name ?? mapping?.[id]?.label)
          .join(', ');
      } else if (GENERIC_SINGLE_SELECT_FIELDS.includes(profileFieldName)) {
        return mapping[profileFieldValue]?.label ?? mapping[profileFieldValue];
      }
      return profileFieldValue;
    default:
      return profileFieldValue;
  }
};

const useProfileFieldValue = () => {
  const { t, dateFormat } = useContext(ConfigContext);
  const { mappedBranches } = useBranches();
  const { mappedPositions } = usePositions();
  const { mappedClients } = useCRM();
  const { mappedDepartments } = useDepartments();
  const { mappedEmploymentTypes } = useEmploymentTypes();
  const { mappedLocations } = useLocations();
  const mappedProfileFields = useMemo(
    () => ({
      branches: mappedBranches,
      positions: mappedPositions,
      departmentId: mappedDepartments,
      blockedClients: mappedClients,
      preferredClients: mappedClients,
      employmentTypeId: mappedEmploymentTypes,
      preferredAreaIds: mappedLocations
    }),
    [mappedBranches, mappedClients, mappedDepartments, mappedEmploymentTypes, mappedLocations, mappedPositions]
  );

  return {
    getProfileFieldValue: ({ profileFieldName, profileFieldType, profileFieldValue }) =>
      getProfileFieldValueByMapping({
        t,
        profileFieldName,
        profileFieldType,
        profileFieldValue,
        mapping: mappedProfileFields[profileFieldName],
        dateFormat
      })
  };
};

export default useProfileFieldValue;
